import { useEffect, useRef, useState, type ReactNode } from "react";

interface DeferUntilVisibleProps {
  children: ReactNode;
  /**
   * Height reserved before the children mount. Must match the rendered height
   * so swapping the placeholder for real content causes no layout shift.
   */
  minHeight: number | string;
  /**
   * How far ahead of the viewport to start mounting. Generous by default:
   * these embeds are sized by their own third-party scripts, so they need
   * room to finish growing while still off screen.
   */
  rootMargin?: string;
  className?: string;
}

/**
 * Mounts its children only once they are close to the viewport.
 *
 * The map and the review widget each pull in a large third-party bundle, and
 * both sit well below the fold — loading them during initial page load costs
 * bandwidth and main-thread time before the visitor has seen anything. An
 * IntersectionObserver defers that work until it is actually needed, while the
 * reserved height keeps the page from shifting when the real content arrives.
 */
const DeferUntilVisible = ({
  children,
  minHeight,
  rootMargin = "600px",
  className,
}: DeferUntilVisibleProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;

    // Without IntersectionObserver, render immediately rather than never.
    if (!("IntersectionObserver" in window)) {
      setShow(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [show, rootMargin]);

  return (
    <div ref={ref} className={className} style={show ? undefined : { minHeight }}>
      {show ? children : null}
    </div>
  );
};

export default DeferUntilVisible;
